import { useEffect, useState, useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, CalendarDays, Clock, Edit3, Plus, AlertCircle, Power, PowerOff, Filter } from "lucide-react";
import { fetchAllPrograms } from "@/lib/api";
import { programApi } from "@/lib/api/program";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { DraftsList } from "@/components/programs/DraftsList";
import { toast } from "sonner";


type Program = {
  _id: string;
  programId?: string;
  title: string;
  subtitle?: string;
  description: string;
  videoUrl?: string;
  language?: string;
  status?: string;
  category?: {
    _id: string;
    title: string;
    description?: string;
    categoryId?: string;
    field?: string;
  };
  programType?: string;
  createdAt?: string;
  updatedAt?: string;
  keyFeatures?: Array<{
    id: number;
    name: string;
    icon: string;
    value: string;
    descriptionHeading?: string;
    description?: string;
  }>;
  // New module-related fields
  payAsYouGo?: boolean;
  moduleConfiguration?: {
    payAsYouGo: boolean;
    modules: Array<{
      _id?: string;
      name: string;
      description?: string;
      programId?: string;
      price: number;
      isActive: boolean;
      order: number;
      metadata?: {
        duration?: number;
        difficulty?: 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED';
        tags?: string[];
        videoUrl?: string;
        resources?: Array<{
          type: 'PDF' | 'VIDEO' | 'AUDIO' | 'LINK';
          title: string;
          url: string;
        }>;
      };
      prerequisites?: string[];
      estimatedCompletionTime?: number;
      isExam?: boolean;
      examDetails?: {
        passingScore: number;
        timeLimit?: number;
        questions?: number;
      };
    }>;
    pricingStrategy?: 'INDIVIDUAL' | 'BUNDLE';
    bundleDiscount?: number;
    minimumModulesForBundle?: number;
  };
  modulePricingMetadata?: {
    totalModules: number;
    totalPrice: number;
    averageModulePrice: number;
    bundlePrice?: number;
    savingsWithBundle?: number;
  };
};

// Add debug logging
const DEBUG = true; // Set to true temporarily for debugging
const log = (...args: any[]) => {
  if (DEBUG) {
    console.log('[Programs]', ...args);
  }
};


const ProgramsContent = () => {
  log('Component rendering');

  // State
  const [programsLoading, setProgramsLoading] = useState(true);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>("published");
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const navigate = useNavigate();

  // Load data
  const loadData = useCallback(async (isRetry = false) => {
    if (isRetry) {
      setIsRetrying(true);
    } else {
      log('loadData started');
      setProgramsLoading(true);
    }
    setError(null);

    try {
      // Load programs only (drafts are handled by DraftsList component)
      const programsResponse = await fetchAllPrograms();

      log('Programs response:', programsResponse);
      log('Response type:', typeof programsResponse);
      log('Response keys:', Object.keys(programsResponse || {}));

      // Handle programs - support multiple response structures
      let programs: Program[] = [];
      
      if (programsResponse?.success === true && programsResponse.programs) {
        programs = programsResponse.programs;
      } else if (programsResponse?.data?.programs) {
        programs = programsResponse.data.programs;
      } else if (Array.isArray(programsResponse)) {
        // Direct array response
        programs = programsResponse;
      } else if (programsResponse?.statusCode === 200 && programsResponse?.data?.programs) {
        programs = programsResponse.data.programs;
      } else {
        throw new Error(programsResponse?.error || programsResponse?.message || 'Failed to load programs');
      }
      
      setPrograms(programs);
      setRetryCount(0); // Reset retry count on success
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "An error occurred while loading data";
      log('Error in loadData:', err);
      
      // Handle specific authentication errors
      if (err instanceof Error && err.message.includes('Authentication failed')) {
        setError('Your session has expired. Please refresh the page to log in again.');
      } else if (err instanceof Error && err.message.includes('Network error')) {
        setError('Unable to connect to the server. Please check your connection and try again.');
      } else {
        setError(errorMessage);
      }
      
      setPrograms([]);
    } finally {
      setProgramsLoading(false);
      setIsRetrying(false);
    }
  }, []);

  // Retry function
  const retryLoadData = useCallback(() => {
    if (retryCount < 3 && !isRetrying) {
      setRetryCount(prev => prev + 1);
      loadData(true);
    }
  }, [retryCount, isRetrying, loadData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Debug render
  log('Current state:', {
    programsLoading,
    error,
    programsCount: programs.length,
  });

  return (
    <div className="container mx-auto py-4 sm:py-6 lg:py-8">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6 sm:mb-8">
        <div className="space-y-2">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Programs</h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            Manage your programs and drafts
          </p>
        </div>
        <Button 
          onClick={() => navigate('/programs/create')}
          className="w-full sm:w-auto"
        >
          <Plus className="h-4 w-4 mr-1" />
          <span className="hidden sm:inline">New Program</span>
          <span className="sm:hidden">New</span>
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="published" className="text-xs sm:text-sm">Published Programs</TabsTrigger>
          <TabsTrigger value="drafts" className="text-xs sm:text-sm">Drafts</TabsTrigger>
        </TabsList>

        <TabsContent value="published" className="mt-4 sm:mt-6">
          <PublishedPrograms
            programs={programs}
            isLoading={programsLoading}
            error={error}
            onRetry={retryLoadData}
            canRetry={retryCount < 3}
            isRetrying={isRetrying}
          />
        </TabsContent>

        <TabsContent value="drafts" className="mt-4 sm:mt-6">
          <DraftsList />
        </TabsContent>
      </Tabs>
    </div>
  );
};

const PublishedPrograms = ({
  programs,
  isLoading,
  error,
  onRetry,
  canRetry,
  isRetrying
}: {
  programs: Program[];
  isLoading: boolean;
  error: string | null;
  onRetry?: () => void;
  canRetry?: boolean;
  isRetrying?: boolean;
}) => {
  const [localPrograms, setLocalPrograms] = useState<Program[]>(programs);
  const [selectedPrograms, setSelectedPrograms] = useState<Set<string>>(new Set());
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  // Update local programs when props change
  useEffect(() => {
    setLocalPrograms(programs);
  }, [programs]);

  // Filter programs based on status
  const filteredPrograms = localPrograms.filter(program => {
    if (statusFilter === 'all') return true;
    return program.status === statusFilter;
  });

  const handleStatusChange = (programId: string, newStatus: string) => {
    setLocalPrograms(prev => 
      prev.map(program => 
        program._id === programId 
          ? { ...program, status: newStatus }
          : program
      )
    );
  };

  const handleProgramSelect = (programId: string, checked: boolean) => {
    setSelectedPrograms(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(programId);
      } else {
        newSet.delete(programId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedPrograms(new Set(filteredPrograms.map(p => p._id)));
    } else {
      setSelectedPrograms(new Set());
    }
  };

  const handleBulkStatusUpdate = async (newStatus: 'active' | 'inactive') => {
    if (selectedPrograms.size === 0) {
      toast.error('Please select at least one program');
      return;
    }

    setIsBulkUpdating(true);
    try {
      const selectedProgramIds = Array.from(selectedPrograms);
      const updatePromises = selectedProgramIds.map(programId =>
        programApi.updateProgram(programId, { status: newStatus })
      );

      const results = await Promise.allSettled(updatePromises);
      const successful = results.filter(result => 
        result.status === 'fulfilled' && result.value.success
      ).length;

      if (successful === selectedProgramIds.length) {
        toast.success(`Successfully ${newStatus === 'active' ? 'activated' : 'deactivated'} ${successful} program${successful > 1 ? 's' : ''}`);
        // Update local state
        setLocalPrograms(prev => 
          prev.map(program => 
            selectedPrograms.has(program._id) 
              ? { ...program, status: newStatus }
              : program
          )
        );
        setSelectedPrograms(new Set());
      } else {
        toast.error(`Failed to update some programs. ${successful}/${selectedProgramIds.length} updated successfully.`);
      }
    } catch (error: any) {
      console.error('Error in bulk status update:', error);
      toast.error('Failed to update program statuses');
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const isAllSelected = filteredPrograms.length > 0 && selectedPrograms.size === filteredPrograms.length;
  // const isIndeterminate = selectedPrograms.size > 0 && selectedPrograms.size < filteredPrograms.length;

  if (isLoading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState message={error} onRetry={onRetry} canRetry={canRetry} isRetrying={isRetrying} />;
  }

  if (!localPrograms.length) {
    return <EmptyState type="published" />;
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Filters and Bulk Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-3 sm:p-4 bg-muted/50 rounded-lg">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Select value={statusFilter} onValueChange={(value: 'all' | 'active' | 'inactive') => setStatusFilter(value)}>
              <SelectTrigger className="w-full sm:w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Programs</SelectItem>
                <SelectItem value="active">Active Only</SelectItem>
                <SelectItem value="inactive">Inactive Only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Selection Controls */}
          <div className="flex items-center gap-2">
            <Checkbox
              checked={isAllSelected}
              onCheckedChange={handleSelectAll}
              disabled={isBulkUpdating}
            />
            <span className="text-xs sm:text-sm font-medium">
              {selectedPrograms.size > 0 
                ? `${selectedPrograms.size} program${selectedPrograms.size > 1 ? 's' : ''} selected`
                : 'Select all'
              }
            </span>
          </div>
        </div>
        
        {selectedPrograms.size > 0 && (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleBulkStatusUpdate('active')}
              disabled={isBulkUpdating}
              className="w-full sm:w-auto"
            >
              <Power className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Activate All</span>
              <span className="sm:hidden">Activate</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleBulkStatusUpdate('inactive')}
              disabled={isBulkUpdating}
              className="w-full sm:w-auto"
            >
              <PowerOff className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Deactivate All</span>
              <span className="sm:hidden">Deactivate</span>
            </Button>
          </div>
        )}
      </div>

      {/* Programs List */}
      {filteredPrograms.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <p className="text-sm sm:text-base">No programs match the current filter.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredPrograms.map((program) => (
            <ProgramListItem 
              key={program._id} 
              program={program} 
              onStatusChange={handleStatusChange}
              isSelected={selectedPrograms.has(program._id)}
              onSelect={handleProgramSelect}
              isBulkUpdating={isBulkUpdating}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ProgramListItem = ({ 
  program, 
  onStatusChange,
  isSelected,
  onSelect,
  isBulkUpdating
}: { 
  program: Program; 
  onStatusChange?: (programId: string, newStatus: string) => void;
  isSelected?: boolean;
  onSelect?: (programId: string, checked: boolean) => void;
  isBulkUpdating?: boolean;
}) => {
  const navigate = useNavigate();
  const [isUpdating, setIsUpdating] = useState(false);

  const handleStatusToggle = async (checked: boolean) => {
    const newStatus = checked ? 'active' : 'inactive';
    
    setIsUpdating(true);
    try {
      const response = await programApi.updateProgram(program._id, {
        status: newStatus
      });

      if (response.success) {
        toast.success(`Program ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
        onStatusChange?.(program._id, newStatus);
      } else {
        toast.error(response.message || 'Failed to update program status');
      }
    } catch (error: any) {
      console.error('Error updating program status:', error);
      toast.error(error.message || 'Failed to update program status');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className={`${isSelected ? "ring-2 ring-primary bg-primary/5" : "bg-card"} border rounded-lg p-4 hover:bg-muted/50 transition-colors`}>
      <div className="flex items-center justify-between gap-4">
        {/* Left section - Selection, Title, Description */}
        <div className="flex items-start gap-3 flex-1 min-w-0">
          {onSelect && (
            <Checkbox
              checked={isSelected}
              onCheckedChange={(checked) => onSelect(program._id, checked as boolean)}
              disabled={isBulkUpdating}
              className="mt-1 flex-shrink-0"
            />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-sm font-semibold truncate">
                {program.title}
              </h3>
              <Badge variant={program.status === 'active' ? 'default' : 'secondary'} className="text-xs">
                {program.status}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
              {program.description}
            </p>
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                <span>Updated {new Date(program.updatedAt || '').toLocaleDateString()}</span>
              </div>
              {program.category && (
                <div className="flex items-center gap-1">
                  <span>•</span>
                  <span>{program.category.title}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right section - Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <Switch
            checked={program.status === 'active'}
            onCheckedChange={handleStatusToggle}
            disabled={isUpdating || isBulkUpdating}
            aria-label={`Toggle ${program.title} status`}
          />
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/programs/${program._id}`)}
              className="h-8 px-2"
            >
              <span className="hidden sm:inline">View</span>
              <span className="sm:hidden">👁</span>
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => navigate(`/programs/${program._id}/edit`)}
              className="h-8 px-2"
            >
              <Edit3 className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const LoadingState = () => (
  <div className="space-y-2">
    {Array.from({ length: 6 }).map((_, i) => (
      <div key={i} className="bg-card border rounded-lg p-4 animate-pulse">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-start gap-3 flex-1">
            <div className="w-4 h-4 bg-muted rounded mt-1"></div>
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-2">
                <div className="h-4 bg-muted rounded w-48"></div>
                <div className="h-5 bg-muted rounded w-16"></div>
              </div>
              <div className="h-3 bg-muted rounded w-full"></div>
              <div className="h-3 bg-muted rounded w-3/4"></div>
              <div className="flex items-center gap-4">
                <div className="h-3 bg-muted rounded w-32"></div>
                <div className="h-3 bg-muted rounded w-24"></div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-10 h-6 bg-muted rounded-full"></div>
            <div className="flex gap-1">
              <div className="w-12 h-8 bg-muted rounded"></div>
              <div className="w-8 h-8 bg-muted rounded"></div>
            </div>
          </div>
        </div>
      </div>
    ))}
    <div className="flex justify-center py-4">
      <div className="flex items-center space-x-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Loading programs...</span>
      </div>
    </div>
  </div>
);

const ErrorState = ({ message, onRetry, canRetry = false, isRetrying = false }: { 
  message: string; 
  onRetry?: () => void; 
  canRetry?: boolean;
  isRetrying?: boolean;
}) => (
  <div className="flex flex-col items-center justify-center py-8 sm:py-12 text-center px-4">
    <AlertCircle className="h-8 w-8 sm:h-12 sm:w-12 text-destructive mb-3 sm:mb-4" />
    <h3 className="text-base sm:text-lg font-semibold">Something went wrong</h3>
    <p className="text-xs sm:text-sm text-muted-foreground mt-2 max-w-md">{message}</p>
    {canRetry && onRetry && (
      <Button 
        onClick={onRetry} 
        disabled={isRetrying}
        className="mt-4"
        variant="outline"
      >
        {isRetrying ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Retrying...
          </>
        ) : (
          'Try Again'
        )}
      </Button>
    )}
  </div>
);

const EmptyState = ({ type }: { type: 'published' | 'drafts' }) => (
  <div className="flex flex-col items-center justify-center py-8 sm:py-12 text-center px-4">
    <div className="rounded-full bg-muted p-3 sm:p-4 mb-3 sm:mb-4">
      {type === 'published' ? (
        <CalendarDays className="h-6 w-6 sm:h-8 sm:w-8 text-muted-foreground" />
      ) : (
        <Edit3 className="h-6 w-6 sm:h-8 sm:w-8 text-muted-foreground" />
      )}
    </div>
    <h3 className="text-base sm:text-lg font-semibold">
      No {type === 'published' ? 'programs' : 'drafts'} yet
    </h3>
    <p className="text-xs sm:text-sm text-muted-foreground mt-2 max-w-md">
      {type === 'published'
        ? 'Get started by creating your first program'
        : 'Create a draft to start working on a new program'}
    </p>
  </div>
);

const Programs = () => {
  return (
    <ErrorBoundary>
      <ProgramsContent />
    </ErrorBoundary>
  );
};

export default Programs;